import { useLegislation } from "routes/schedule/lib/use-legislation"
import { legislationHref } from "routes/schedule/lib/legislation-href.mjs"
import { useEffect, useRef, useState } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { Box, Button, DialogContent, DialogTitle, IconButton, List, ListItemButton, ListItemText } from "@mui/material"
import { DebouncedTextField } from "lib/@components/debounced-text-field"
import { setFromEvent, withinTransition } from "lib/setFromEvent"
import { useDialog } from "lib/@hooks/useDialog"
import stringSimilarity from "string-similarity"
import { escapeRegExp } from "library/safe-regexp.mjs"
import { DisplayLegislationItem } from "event-definitions"
import { ListItemBox } from "lib/@components/ListItemBox"
import { IsEditMode, NotEditMode } from "lib/@components/edit-mode"
import { prevent } from "lib/prevent"
import { Sortable } from "lib/@components/Sortable"
import { DragHandle } from "lib/dragHandle"
import { useRefresh } from "lib/@hooks/useRefresh"
import { Repeat } from "lib/@components/repeat"
import { SortableItem } from "lib/@components/SortableItem"
import { OkCancel } from "lib/@components/okCancel"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import Iconify from "minimals-template/components/Iconify"
import { GuidanceOption } from "./guidance-option"

export function Guidance() {
    const { target, editMode } = useBoundContext()
    const legislation = useLegislation()
    const refresh = useRefresh()
    target.legislation ??= []
    return (
        <Bound refresh={refresh} legislation={legislation}>
            <IsEditMode>
                <Sortable items={target.legislation} onOrdered={orderItems}>
                    <List sx={{ maxHeight: editMode ? 345 : undefined, overflow: "auto" }}>
                        <Repeat
                            list={target.legislation.sortBy("HTML")}
                            key={(v) => v.URL}
                            item={<DraggableLegislationItem />}
                        />
                    </List>
                </Sortable>

                <AddLegislation />
            </IsEditMode>
            <NotEditMode>
                <List data-cy="schedule-references" sx={{ maxHeight: editMode ? 345 : undefined, overflow: "auto" }}>
                    <Repeat list={target.legislation.sortBy("HTML")} key={(v) => v.URL} item={<LegislationItem />} />
                </List>
            </NotEditMode>
        </Bound>
    )

    function orderItems() {
        refresh()
    }
}

function AddLegislation() {
    const { refresh, target } = useBoundContext()
    const dialog = useDialog(<AddLegislationDialog target={target} />, { maxWidth: "lg" })
    return (
        <Button data-cy="add-reference-button" sx={{ mt: 2 }} color="primary" onClick={add} id="add-guidance">
            + Add Reference
        </Button>
    )

    async function add() {
        const guidance = await dialog()
        if (guidance) {
            target.legislation.push(guidance.ScheduleLegislationID)
            DisplayLegislationItem(guidance.ScheduleLegislationID).once((el) => el.scrollIntoView({ block: "nearest" }))
            refresh()
        }
    }
}

function AddLegislationDialog({ target }) {
    const legislation = useLegislation()
    const [search, setSearch] = useState("")
    const [current, setCurrent] = useState(null)
    const ref = useRef()
    return (
        <Bound legislation={legislation}>
            <DialogTitle>Add Reference</DialogTitle>
            <DialogContent sx={{ p: 0, px: 2, py: 0, m: 0, display: "flex", flexDirection: "column" }}>
                <DebouncedTextField
                    data-cy="reference-search-input"
                    sx={{ mt: 2, mb: 1 }}
                    autoFocus
                    size="small"
                    className="guidance-search"
                    value={search}
                    onChange={withinTransition(setFromEvent(updateSearch))}
                    placeholder={"Search"}
                    fullWidth
                />
                <Box
                    ref={ref}
                    sx={{
                        mb: 2,
                        maxHeight: 300,
                        borderRadius: 1,
                        height: 300,
                        bgcolor: "background.default",
                        overflow: "auto",
                    }}
                    width={1}
                >
                    <List>
                        <Repeat
                            list={filter(sort())}
                            item={<GuidanceOption current={current} setCurrent={setCurrent} />}
                            keyFn="ScheduleLegislationID"
                        />
                    </List>
                </Box>
            </DialogContent>

            <OkCancel value={current} />
        </Bound>
    )

    function filter(items) {
        const existing = new Set(target.legislation.map((l) => l.ScheduleLegislationID || l.toString()))
        return items.filter((i) => !existing.has(i.ScheduleLegislationID))
    }

    function updateSearch(term) {
        if (ref && ref.current) ref.current.scrollTop = 0
        setCurrent(null)
        setSearch(term)
    }

    function sort() {
        if (!search.trim()) return legislation
        return legislation
            .map((legislation) => ({
                legislation,
                score: compare(legislation.HTML, search),
            }))
            .sortBy("score")
            .filter((s) => s.score < -0.3)
            .map("legislation")
            .slice(0, 125)
    }

    function compare(s1, s2) {
        s1 = s1.toLowerCase()
        s2 = s2.toLowerCase()
        const parts = s2.split(" ")
        const offset = parts.filter(Boolean).reduce(applyComparison, 0)
        return offset - stringSimilarity.compareTwoStrings(s1, s2)

        function applyComparison(a, c) {
            let result = s1.startsWith(c)
                ? a - 5
                : s1.match(new RegExp(`\\s${escapeRegExp(c)}\\s`)) // eslint-disable-line security/detect-non-literal-regexp
                  ? a - 3
                  : s1.includes(c)
                    ? a - 1
                    : a
            if (result !== a) {
                result -= (s1.length - s1.indexOf(c)) / (s1.length / 2)
            }
            return result
        }
    }
}

function DraggableLegislationItem({ item }) {
    const [props, setProps] = useState({})

    return (
        <SortableItem id={item} setDragProps={setProps}>
            <LegislationItem item={item} extra={<DragHandle {...props} />} />
        </SortableItem>
    )
}

function LegislationItem({ item, extra = null }) {
    const { target, refresh, legislation } = useBoundContext()
    item = typeof item !== "object" ? legislation.find((i) => i.ScheduleLegislationID === item.toString()) ?? {} : item
    const { URL, HTML } = item
    const ref = useRef()
    useEffect(() => {
        if (!item?.ScheduleLegislationID) return
        DisplayLegislationItem(item.ScheduleLegislationID).raise(ref.current)
    }, [item])

    return (
        !!HTML && (
            <ListItemButton
                data-cy="schedule-reference-item"
                ref={ref}
                size="small"
                onClick={() => window.open(legislationHref(URL), "legislation")}
            >
                <ListItemBox>
                    {extra}
                    <Box sx={{ mr: 1, color: "primary.main" }}>
                        <Iconify icon="ci:external-link" />
                    </Box>
                    <ListItemText primary={HTML} />
                    <Box flex={1} />
                    <IsEditMode>
                        <IconButton
                            aria-label="Remove reference"
                            data-cy="delete-reference-button"
                            onClick={prevent(remove)}
                            color="secondary"
                        >
                            <Iconify icon="mdi:delete" />
                        </IconButton>
                    </IsEditMode>
                </ListItemBox>
            </ListItemButton>
        )
    )

    function remove() {
        const index = target.legislation.findIndex((i) => `${i}` === `${item.ScheduleLegislationID}`)
        if (index === -1) {
            return
        }
        target.legislation.splice(index, 1)

        refresh()
    }
}
